import { Injectable, computed, inject } from '@angular/core';
import {
  IChannel357,
  IChannel357Collection,
  IChannelSpam,
  IChannelSpamCollection,
  IChannelUpSale,
  IChannelsData,
  IChannelsModel,
  IHttpResponse
} from '@partners/api';
import { HttpService, tapResult } from '@partners/http';
import { ChannelsStore } from '@partners/state';
import { IChannelSpamStatus, IChannelUpsaleStatus } from '@partners/types';
import { isNotEmpty } from '@partners/utils';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ChannelsFacade {
  private _httpService = inject(HttpService);
  private _channelsStore = inject(ChannelsStore);

  readonly model$$ = computed(() => this._channelsStore.model$$());
  readonly data$$ = computed(() => this._channelsStore.data$$());

  readonly channels357Model$$ = computed(() => this._channelsStore.channels357Model$$());
  readonly spamModel$$ = computed(() => this._channelsStore.spamModel$$());
  readonly upSaleModel$$ = computed(() => this._channelsStore.upSaleModel$$());

  readonly isUpSaleMail$$ = computed(() => this.data$$()?.isUpSaleMail);
  readonly isUpSaleSms$$ = computed(() => this.data$$()?.isUpSaleSms);

  readonly isRegisteredBanking$$ = computed<boolean>(() => {
    const bankingChannels: IChannel357Collection = this.channels357Model$$();
    const isAllReg = Object.values(bankingChannels).every((value: IChannel357) => value.isClientRegistered);
    return isAllReg;
  });

  readonly spamStatus$$ = computed<IChannelSpamStatus>(() => ({
    mail: this.spamModel$$().spamMail?.levelID,
    sms: this.spamModel$$().spamSms?.levelID
  }));

  readonly upsaleStatus$$ = computed<IChannelUpsaleStatus>(() => ({
    mail: this.upSaleModel$$().calMail?.levelID,
    sms: this.upSaleModel$$().calSms?.levelID
  }));

  readonly isRegisteredInternet$$ = computed<boolean>(() => {
    const { internet } = this.channels357Model$$();
    return internet.isClientRegistered;
  });

  readonly isRegisteredTelephony$$ = computed<boolean>(() => {
    const { telephony } = this.channels357Model$$();
    return telephony.isClientRegistered;
  });

  readonly isRegisteredSpam$$ = computed<boolean>(() => {
    const spamChannels: IChannelSpamCollection = this.spamModel$$();
    const isAllReg = Object.values(spamChannels).every((value: IChannelSpam) => value.isClientRegisteredService);
    return isAllReg;
  });

  readonly isRegisteredSpamMail$$ = computed<boolean>(() => {
    const { spamMail } = this.spamModel$$();
    return spamMail.isClientRegisteredService;
  });

  readonly isRegisteredSpamSms$$ = computed<boolean>(() => {
    const { spamSms } = this.spamModel$$();
    return spamSms.isClientRegisteredService;
  });

  readonly isRegisteredUpSale$$ = computed<boolean>(() => {
    const upSaleChannels: IChannelSpamCollection = this.spamModel$$();
    const isAllReg = Object.values(upSaleChannels).every((value: IChannelUpSale) => value.isClientRegisteredService);
    return isAllReg;
  });

  readonly isRegisteredAll$$ = computed<boolean>(() => {
    return this.isRegisteredBanking$$() && this.isRegisteredSpam$$() && this.isRegisteredUpSale$$();
  });

  constructor() {}

  loadChannels(model?: IChannelsModel): Observable<IHttpResponse<IChannelsData>> {
    return this._httpService.fetch<IChannelsModel, IChannelsData>('banking').pipe(
      tapResult<IChannelsData>((data: IChannelsData) => {
        const model = this.createModelFromData(data);
        this._channelsStore.dispatchChannelsLoadSuccess({ model, data });
      })
    );
  }

  /**
   * @description
   * we need to copy the model data from the http response's data object into the model
   * object, this is far from optimal architecture, however we are plagued by a diseased
   * server architecture, until it is refactored - we suffer in silence.
   *
   * @param { channel357, upsales, spam } a destruction of a channels data object
   * @returns a model object with data from the server
   */
  private createModelFromData(channelsData: IChannelsData): IChannelsModel {
    const { channel357, upsales, spam } = channelsData;

    channelsData.isUpSaleMail = isNotEmpty(upsales?.calMail);
    channelsData.isUpSaleSms = isNotEmpty(upsales?.calMail);

    let model: IChannelsModel = {
      banking: {
        word: null,
        channel357: {
          internet: undefined,
          telephony: undefined
        },
        spam: {
          spamMail: undefined,
          spamSms: undefined
        },
        upsales: {
          calMail: undefined,
          calSms: undefined
        }
      }
    };
    if (channel357?.internet) {
      model.banking.channel357.internet = {
        ...model.banking.channel357.internet,
        ...channel357.internet
      };
    }
    if (channel357?.telephony) {
      model.banking.channel357.telephony = {
        ...model.banking.channel357.telephony,
        ...channel357.telephony
      };
    }

    if (spam?.spamMail) {
      model.banking.spam.spamMail = {
        ...model.banking.spam.spamMail,
        ...spam.spamMail
      };
    }
    if (spam?.spamSms) {
      model.banking.spam.spamSms = {
        ...model.banking.spam.spamSms,
        ...spam.spamSms
      };
    }

    if (upsales?.calMail) {
      model.banking.upsales.calMail = {
        ...model.banking.upsales.calMail,
        ...upsales.calMail
      };
    }
    if (upsales?.calSms) {
      model.banking.upsales.calSms = {
        ...model.banking.upsales?.calSms,
        ...upsales?.calSms
      };
    }

    return model;
  }
}
